import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, type Pagina } from '../comun';
import type { components } from '../tipos/documentos';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

/** La página para firmar (9.12, por el enlace del mensaje) y «Mis documentos», con los tipos de `documentos.yaml`. */
export type EnlaceDeFirma = Esquema<'PublicSignatureLink'>;
export type SesionDeFirma = Esquema<'SigningSession'>;
export type MiDocumento = Esquema<'MyDocument'>;
export type ListaMisDocumentos = Esquema<'MyDocumentList'>;
export type ArchivoFirmado = Esquema<'FileLink'>;

const enlace = (negocioId: string, token: string): `/v1/${string}` => `/v1/public/businesses/${encodeURIComponent(negocioId)}/signature-links/${encodeURIComponent(token)}`;
const misDocumentos = (perfilId: string): `/v1/${string}` => `/v1/customer-account/profiles/${encodeURIComponent(perfilId)}/documents`;

/**
 * Operaciones de Documentos y Firma en las páginas del cliente: las del enlace de firma, anónimas (el token del enlace,
 * sin sesión ni clave); las de «Mis documentos», con el token de cliente.
 */
export function operacionesDocumentosCliente(api: ClienteApi) {
    return {
        /** getSignatureLink (anónima): título, negocio, plazo y texto; `404` si el enlace no existe, caducó o ya no está pendiente. */
        getSignatureLink: (negocioId: string, token: string) => api.pedir<EnlaceDeFirma>('GET', enlace(negocioId, token), { sinSesion: true }),
        /** startSigningSessionByLink (anónima): la sesión del proveedor; `return_url` solo https (G475). `502` si no responde. */
        startSigningSessionByLink: (negocioId: string, token: string, vuelta: string | null, clave = claveIdempotencia()) =>
            api.pedir<SesionDeFirma>('POST', `${enlace(negocioId, token)}/signing-sessions`, { cuerpo: { ...(vuelta === null ? {} : { return_url: vuelta }), locale: 'es-ES' }, sinSesion: true, idempotencia: clave }),
        /** listMyDocuments (token de cliente): los de un negocio. */
        listMyDocuments: (perfilId: string, pagina: Pagina = {}) => api.pedir<ListaMisDocumentos>('GET', `${misDocumentos(perfilId)}${consulta({ ...pagina })}`),
        /** getMySignedDocumentFile: dirección temporal (5 min) del PDF firmado. */
        getMySignedDocumentFile: (perfilId: string, documentoId: string) => api.pedir<ArchivoFirmado>('GET', `${misDocumentos(perfilId)}/${encodeURIComponent(documentoId)}/file`),
        /** startMySigningSession: firmar desde «Mis documentos» (`can_sign`). */
        startMySigningSession: (perfilId: string, documentoId: string, vuelta: string | null, clave = claveIdempotencia()) =>
            api.pedir<SesionDeFirma>('POST', `${misDocumentos(perfilId)}/${encodeURIComponent(documentoId)}/signing-sessions`, { cuerpo: { ...(vuelta === null ? {} : { return_url: vuelta }), locale: 'es-ES' }, idempotencia: clave }),
    };
}
